import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../utils/apiConfig";
import { useAuth } from "../contexts/AuthContext";
import { completeLoginSession } from "../utils/loginCompletion";
import {
    LoginResponse,
    MfaChallengeResponse,
    MfaVerifyErrorResponse,
} from "../types/guardian";
import polarisLogo from "../images/Polaris Logo.svg";
import "../styles/login-page.css";

const CODE_LENGTH = 6;

export function MfaVerifyPage() {
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();

    const [challengeId, setChallengeId] = useState<string | null>(null);
    const [maskedEmail, setMaskedEmail] = useState<string>("");
    const [code, setCode] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);

    // Challenge metadata is set by LoginPage before navigating here; without it, restart login.
    useEffect(() => {
        const storedChallengeId = sessionStorage.getItem("mfaChallengeId");
        if (!storedChallengeId) {
            navigate("/login", { replace: true });
            return;
        }
        setChallengeId(storedChallengeId);
        setMaskedEmail(sessionStorage.getItem("mfaMaskedEmail") ?? "");
    }, [navigate]);

    useEffect(() => {
        if (resendCooldownSeconds <= 0) return;
        const timer = window.setInterval(() => {
            setResendCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [resendCooldownSeconds]);

    function clearChallenge() {
        sessionStorage.removeItem("mfaChallengeId");
        sessionStorage.removeItem("mfaMaskedEmail");
        sessionStorage.removeItem("mfaExpiresInSeconds");
    }

    function handleBackToLogin() {
        if (challengeId) {
            // Best-effort abandon so the pending challenge can't be reused.
            void fetch(buildApiUrl("api/auth/mfa/cancel"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ challengeId }),
            }).catch(() => undefined);
        }
        clearChallenge();
        navigate("/login");
    }

    async function handleResend() {
        setError("");
        setSuccessMessage("");

        if (!challengeId) {
            navigate("/login", { replace: true });
            return;
        }
        if (resendCooldownSeconds > 0 || isResending || isSubmitting) {
            return;
        }

        setIsResending(true);
        try {
            const res = await fetch(buildApiUrl("api/auth/mfa/resend"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ challengeId }),
            });

            if (res.ok) {
                const body = (await res.json()) as MfaChallengeResponse;
                if (body.mfaChallengeId) {
                    sessionStorage.setItem("mfaChallengeId", body.mfaChallengeId);
                    setChallengeId(body.mfaChallengeId);
                }
                if (body.expiresInSeconds != null) {
                    sessionStorage.setItem("mfaExpiresInSeconds", String(body.expiresInSeconds));
                }
                if (body.maskedEmail) {
                    sessionStorage.setItem("mfaMaskedEmail", body.maskedEmail);
                    setMaskedEmail(body.maskedEmail);
                }
                setCode("");
                setSuccessMessage(
                    maskedEmail
                        ? `A new code has been sent to ${maskedEmail}.`
                        : "A new code has been sent to your email."
                );
                setResendCooldownSeconds(60);
                return;
            }

            const body = (await res.json().catch(() => ({}))) as MfaVerifyErrorResponse;

            if (res.status === 410 || body.mfaExpired) {
                clearChallenge();
                navigate("/login?error=mfa_expired", { replace: true });
                return;
            }

            if (res.status === 429) {
                const retryAfter = body.retryAfterSeconds ?? 60;
                setResendCooldownSeconds(retryAfter);
                setError(`Please wait ${retryAfter} seconds before requesting another code.`);
                return;
            }

            setError("Unable to resend the code. Please log in again.");
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setIsResending(false);
        }
    }

    async function handleSubmit(ev: React.FormEvent) {
        ev.preventDefault();
        setError("");
        setSuccessMessage("");

        if (!challengeId) {
            navigate("/login", { replace: true });
            return;
        }
        if (code.length !== CODE_LENGTH) {
            setError(`Please enter the ${CODE_LENGTH}-digit code from your email.`);
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await fetch(buildApiUrl("api/auth/mfa/verify"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ challengeId, code }),
            });

            if (res.ok) {
                const loginData = (await res.json()) as LoginResponse;
                clearChallenge();
                await completeLoginSession(loginData, navigate, refreshAuth);
                return;
            }

            const body = (await res.json().catch(() => ({}))) as MfaVerifyErrorResponse;

            if (res.status === 410 || body.mfaExpired) {
                clearChallenge();
                navigate("/login?error=mfa_expired", { replace: true });
                return;
            }

            if (body.mfaLocked) {
                clearChallenge();
                navigate("/login?error=mfa_locked", { replace: true });
                return;
            }

            const remaining = body.attemptsRemaining;
            setCode("");
            setError(
                remaining != null
                    ? `Invalid code. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
                    : "Invalid code. Please try again."
            );
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    const resendDisabled = isSubmitting || isResending || resendCooldownSeconds > 0;
    const resendLabel = resendCooldownSeconds > 0
        ? `Resend code (${resendCooldownSeconds}s)`
        : isResending
            ? "Sending..."
            : "Resend code";

    return (
        <main className="login-page" style={{ backgroundColor: "#000000" }} data-testid="mfa-verify-page">
            <div className="login-overlay" style={{ backgroundColor: "#00000066" }} />

            <section className="login-card" role="dialog" aria-label="Enter verification code">
                <header className="login-head">
                    <img src={polarisLogo} alt="Polaris Athlete" className="login-logo" />
                    <p className="welcome">
                        Enter the {CODE_LENGTH}-digit verification code we emailed
                        {maskedEmail ? <> to <strong>{maskedEmail}</strong></> : null}.
                    </p>
                </header>

                <form onSubmit={handleSubmit} className="login-form">
                    {error && <div className="form-alert is-error" role="alert">{error}</div>}
                    {successMessage && (
                        <div className="form-alert is-success" role="status">{successMessage}</div>
                    )}

                    <div className="field">
                        <label htmlFor="mfa-code">Verification code</label>
                        <input
                            id="mfa-code"
                            name="mfa-code"
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            pattern="\d*"
                            maxLength={CODE_LENGTH}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
                            placeholder="000000"
                            className="input mfa-code-input"
                            autoFocus
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="login-btn-primary"
                        disabled={isSubmitting || code.length !== CODE_LENGTH}
                    >
                        {isSubmitting ? "Verifying..." : "Verify code"}
                    </button>

                    <button
                        type="button"
                        className="mfa-back-link"
                        onClick={handleResend}
                        disabled={resendDisabled}
                    >
                        {resendLabel}
                    </button>

                    <button
                        type="button"
                        className="mfa-back-link"
                        onClick={handleBackToLogin}
                    >
                        Back to login
                    </button>
                </form>
            </section>
        </main>
    );
}

export default MfaVerifyPage;
